import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Camera, StopCircle, Loader2, QrCode, CheckCircle2, AlertTriangle, XCircle, CloudOff } from 'lucide-react';
import { cwPrettyDate, cwTodayISO, cwTimeHM, nightsBetween } from '../lib/format';
import { BookingDetailDrawer } from '../components/BookingDetailDrawer';
import { useRegisterStatus } from '../context/RegisterStatus';
import { enqueueCheckIn, getQueue, flushQueue, subscribeQueue } from '../lib/offlineQueue';

// State machine:
//  idle        -> camera or manual can start scan
//  looking-up  -> waiting for booking lookup
//  green       -> today's arrival, one-click check in
//  amber       -> confirmed but date mismatch OR already checked in
//  red         -> not-found / held / cancelled -> no check-in
//  done        -> just checked in, show success and auto-return after 2s

export default function Scanner() {
  const [manual, setManual] = useState('');
  const [phase, setPhase] = useState('idle');
  const [card, setCard] = useState(null); // { colour, booking?, reason?, allowOverride?, notFoundBn? }
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraErr, setCameraErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [viewingBn, setViewingBn] = useState(null);
  const [queueSize, setQueueSize] = useState(getQueue().length);
  const [online, setOnline] = useState(navigator.onLine);
  const { isStale } = useRegisterStatus() || {};
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanningRef = useRef(false);

  // ---- Look up + classify a scanned code
  const lookup = useCallback(async (raw) => {
    const bn = (raw || '').trim();
    if (!bn) return;
    setPhase('looking-up');
    try {
      const { data } = await api.get(`/bookings/${encodeURIComponent(bn)}`);
      const b = data.booking;
      const today = cwTodayISO();

      if (b.Status === 'Held') {
        setCard({ colour: 'red', booking: b, reason: 'Payment not yet confirmed. Send the guest to the desk.' });
        setPhase('red'); return;
      }
      if (b.Status === 'Cancelled-Released') {
        setCard({ colour: 'red', booking: b, reason: 'This booking was released and the room is no longer held.' });
        setPhase('red'); return;
      }
      // Confirmed
      if (b.CheckedInAt) {
        setCard({
          colour: 'amber',
          booking: b,
          reason: `Already checked in at ${cwTimeHM(b.CheckedInAt)}${b.CheckedInBy ? ` by ${b.CheckedInBy}` : ''}.`,
          allowOverride: false,
        });
        setPhase('amber'); return;
      }
      if (b.CheckIn === today) {
        setCard({ colour: 'green', booking: b });
        setPhase('green'); return;
      }
      // date mismatch
      const [y, m, d] = b.CheckIn.split('-').map(Number);
      const [ty, tm, td] = today.split('-').map(Number);
      const days = Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / 86400000);
      const monthName = ['January','February','March','April','May','June','July','August','September','October','November','December'][m-1];
      let reason;
      if (days > 0) {
        reason = `Arriving on ${d} ${monthName}, not today.`;
      } else {
        reason = `Was due on ${d} ${monthName}, ${-days} day${-days===1?'':'s'} ago.`;
      }
      setCard({ colour: 'amber', booking: b, reason, allowOverride: true, overrideReasonAuto: reason });
      setPhase('amber');
    } catch (e) {
      if (e.response?.status === 404) {
        setCard({ colour: 'red', reason: 'No booking found for this code.', notFoundBn: bn });
      } else {
        setCard({ colour: 'red', reason: e.response?.data?.detail || 'Could not look up the booking.' });
      }
      setPhase('red');
    }
  }, []);

  // ---- Camera
  const stopCamera = useCallback(() => {
    scanningRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraErr('');
    if (!('BarcodeDetector' in window)) {
      setCameraErr("This browser can't read QR codes with the camera. Type the booking number below.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setCameraOn(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      scanningRef.current = true;
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const loop = async () => {
        if (!scanningRef.current || !videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes.length) {
            const raw = codes[0].rawValue || '';
            scanningRef.current = false; // pause while we look up
            await lookup(raw);
            return;
          }
        } catch {}
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
    } catch {
      setCameraErr('Camera is unavailable. Type the booking number below instead.');
      setCameraOn(false);
    }
  }, [lookup]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  // Auto-attempt camera on mount for mobile-first flow
  useEffect(() => {
    // Only prompt if the API is present so we don't spam permission dialogs
    if ('BarcodeDetector' in window && 'mediaDevices' in navigator) {
      startCamera();
    } else {
      setCameraErr("This browser can't read QR codes with the camera. Type the booking number below.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Actions
  const resetToScan = useCallback(() => {
    setCard(null);
    setManual('');
    setPhase('idle');
    if (streamRef.current) {
      scanningRef.current = true;
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const loop = async () => {
        if (!scanningRef.current || !videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes.length) {
            scanningRef.current = false;
            await lookup(codes[0].rawValue || '');
            return;
          }
        } catch {}
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
    } else {
      startCamera();
    }
  }, [lookup, startCamera]);

  const doCheckIn = async (override) => {
    if (!card?.booking) return;
    const bn = card.booking['Booking#'];
    setBusy(true);
    try {
      const body = { booking_number: bn };
      if (override) body.override_reason = card.overrideReasonAuto || 'Date-mismatch override at scanner';
      await api.post('/bookings/check-in', body);
      setPhase('done');
      toast.success('Checked in');
      setTimeout(resetToScan, 2000);
    } catch (e) {
      const status = e?.response?.status;
      const isNetwork = !e?.response;
      if (isNetwork) {
        // Wifi down: queue and show optimistic success. The flusher will retry.
        const body = { booking_number: bn };
        if (override) body.override_reason = card.overrideReasonAuto || 'Date-mismatch override at scanner';
        enqueueCheckIn(body);
        setPhase('queued');
        toast.info(`${card.booking.Name} queued to sync when the wifi is back`);
        setTimeout(resetToScan, 2500);
      } else {
        toast.error(e.response?.data?.detail || 'Could not check in');
      }
    } finally { setBusy(false); }
  };

  // Offline queue watcher + background flusher.
  useEffect(() => {
    const update = () => {
      setQueueSize(getQueue().length);
      setOnline(navigator.onLine);
    };
    const unsub = subscribeQueue(update);
    let interval = null;
    const tryFlush = async () => {
      if (!navigator.onLine || getQueue().length === 0) return;
      const { synced, failed } = await flushQueue(
        (entry) => api.post('/bookings/check-in', entry),
      );
      if (synced) toast.success(`Synced ${synced} queued check-in${synced === 1 ? '' : 's'}`);
      if (failed) toast.error(`${failed} check-in${failed === 1 ? '' : 's'} still to sync`);
    };
    const ensureInterval = () => {
      if (!interval && getQueue().length > 0) {
        interval = setInterval(() => {
          if (getQueue().length === 0) { clearInterval(interval); interval = null; return; }
          tryFlush();
        }, 15000);
      }
    };
    const onQueueChange = () => { update(); ensureInterval(); };
    window.addEventListener('bb-queue-updated', onQueueChange);
    tryFlush().then(ensureInterval);
    const onOnline = () => tryFlush().then(ensureInterval);
    window.addEventListener('online', onOnline);
    return () => {
      unsub();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('bb-queue-updated', onQueueChange);
      if (interval) clearInterval(interval);
    };
  }, []);

  const viewBooking = () => {
    if (card?.booking) setViewingBn(card.booking['Booking#']);
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-3xl sm:text-4xl text-[#1F5A24]">Scanner</h1>
        <p className="text-[#333]/70 mt-1">Point the camera at the guest's QR, or type the booking number.</p>
      </div>

      {/* Camera viewport */}
      <div className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl p-4 sm:p-5">
        <div className="aspect-square w-full bg-black rounded-xl overflow-hidden grid place-items-center relative">
          {cameraOn ? (
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
          ) : (
            <div className="text-white/70 grid place-items-center gap-2 p-6 text-center">
              <QrCode size={56} />
              <span className="text-sm">{cameraErr || 'Camera off'}</span>
            </div>
          )}
          {cameraOn && phase === 'idle' && (
            <div className="absolute inset-6 border-2 border-white/70 rounded-xl pointer-events-none" />
          )}
          {phase === 'looking-up' && (
            <div className="absolute inset-0 bg-black/50 grid place-items-center text-white">
              <Loader2 className="animate-spin" size={28} />
            </div>
          )}
        </div>

        <div className="mt-3 flex gap-2">
          {!cameraOn ? (
            <button
              data-testid="scanner-start"
              onClick={startCamera}
              className="flex-1 min-h-[3rem] inline-flex items-center justify-center gap-2 rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium"
            >
              <Camera size={16}/> Start camera
            </button>
          ) : (
            <button
              data-testid="scanner-stop"
              onClick={stopCamera}
              className="flex-1 min-h-[3rem] inline-flex items-center justify-center gap-2 rounded-lg border border-[#CFD8CF] hover:bg-[#F5F5F5] font-medium"
            >
              <StopCircle size={16}/> Stop camera
            </button>
          )}
        </div>

        {/* Manual entry — always visible; front-and-centre when camera errored */}
        <div className={`mt-5 pt-5 border-t border-[#CFD8CF] ${cameraErr && !cameraOn ? 'bg-[#FFF3E0]/40 -mx-4 -mb-4 sm:-mx-5 sm:-mb-5 px-4 pb-4 sm:px-5 sm:pb-5 rounded-b-2xl' : ''}`}>
          <label className="block text-sm font-medium mb-1">Booking number</label>
          <div className="flex gap-2">
            <input
              data-testid="scanner-manual"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') lookup(manual); }}
              placeholder="BK-…"
              className="flex-1 bg-white border border-[#CFD8CF] rounded-lg px-4 py-3 min-h-[3rem] font-mono"
            />
            <button
              data-testid="scanner-submit"
              onClick={() => lookup(manual)}
              disabled={phase === 'looking-up' || !manual.trim()}
              className="px-5 py-3 min-h-[3rem] rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium disabled:opacity-60"
            >
              Look up
            </button>
          </div>
        </div>
      </div>

      {/* Result card */}
      {phase === 'green' && card?.booking && (
        <ResultCard testid="scanner-card-green" bg="#E8F5E9" border="#2E7D32">
          <Header icon={<CheckCircle2 size={28} className="text-[#2E7D32]" />} title="Welcome" tone="#1F5A24" />
          <GuestBlock b={card.booking} />
          <button
            data-testid="scanner-checkin-btn"
            onClick={() => doCheckIn(false)}
            disabled={busy || isStale}
            title={isStale ? 'Paused — the register may be out of date' : ''}
            className="mt-5 w-full min-h-[3.5rem] rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white text-lg font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? 'Checking in…' : 'Check In'}
          </button>
        </ResultCard>
      )}

      {phase === 'amber' && card?.booking && (
        <ResultCard testid="scanner-card-amber" bg="#FFF3E0" border="#B26A00">
          <Header icon={<AlertTriangle size={28} className="text-[#B26A00]" />} title="Needs a second look" tone="#B26A00" />
          <div className="mt-2 text-[#333]" data-testid="scanner-amber-reason">{card.reason}</div>
          <GuestBlock b={card.booking} />
          <div className="mt-5 grid gap-2">
            {card.allowOverride ? (
              <button
                data-testid="scanner-override-btn"
                onClick={() => doCheckIn(true)}
                disabled={busy || isStale}
                title={isStale ? 'Paused — the register may be out of date' : ''}
                className="w-full min-h-[3.5rem] rounded-lg bg-[#B26A00] hover:bg-[#8f5500] text-white text-lg font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy ? 'Working…' : 'Check in anyway'}
              </button>
            ) : (
              <button
                data-testid="scanner-view-btn"
                onClick={viewBooking}
                className="w-full min-h-[3.5rem] rounded-lg border border-[#B26A00] text-[#B26A00] hover:bg-[#FFF3E0] font-medium"
              >
                View booking
              </button>
            )}
            <button onClick={resetToScan} className="w-full min-h-[2.75rem] text-sm text-[#333]/70 hover:text-[#333]">
              Scan the next guest
            </button>
          </div>
        </ResultCard>
      )}

      {phase === 'red' && (
        <ResultCard testid="scanner-card-red" bg="#FDECEA" border="#C62828">
          <Header icon={<XCircle size={28} className="text-[#C62828]" />} title="Do not issue a key" tone="#8B1A1A" />
          <div className="mt-2 text-[#333]" data-testid="scanner-red-reason">{card?.reason}</div>
          {card?.booking && <GuestBlock b={card.booking} />}
          <div className="mt-5 grid gap-2">
            {card?.booking && (
              <button
                data-testid="scanner-view-btn"
                onClick={viewBooking}
                className="w-full min-h-[3.5rem] rounded-lg border border-[#C62828] text-[#8B1A1A] hover:bg-[#FDECEA] font-medium"
              >
                View booking
              </button>
            )}
            <button onClick={resetToScan} className="w-full min-h-[2.75rem] text-sm text-[#333]/70 hover:text-[#333]">
              Scan the next guest
            </button>
          </div>
        </ResultCard>
      )}

      {phase === 'done' && card?.booking && (
        <ResultCard testid="scanner-card-done" bg="#C8E6C9" border="#1F5A24">
          <div className="grid place-items-center py-4 text-center">
            <CheckCircle2 size={64} className="text-[#1F5A24]" aria-hidden />
            <div className="mt-3 font-heading text-2xl text-[#1F5A24]">{card.booking.Name} is checked in</div>
            <div className="text-sm text-[#333]/70 mt-1">Returning to the camera…</div>
          </div>
        </ResultCard>
      )}

      {phase === 'queued' && card?.booking && (
        <ResultCard testid="scanner-card-queued" bg="#FFF3E0" border="#B26A00">
          <div className="grid place-items-center py-4 text-center">
            <CloudOff size={56} className="text-[#B26A00]" aria-hidden />
            <div className="mt-3 font-heading text-2xl text-[#8f5500]">{card.booking.Name} — will sync</div>
            <div className="text-sm text-[#333]/80 mt-1">
              Marked as checked in. We'll send it to the register the moment the wifi is back.
            </div>
          </div>
        </ResultCard>
      )}

      {(queueSize > 0) && (
        <div
          data-testid="scanner-queue-indicator"
          role="status"
          className="mt-4 rounded-xl border border-[#B26A00] bg-[#FFF3E0] text-[#8f5500] px-4 py-3 flex items-center gap-2"
        >
          <CloudOff size={16} aria-hidden />
          <div className="flex-1 text-sm">
            <strong>{queueSize}</strong> check-in{queueSize === 1 ? '' : 's'} waiting to sync
            {online ? ' — trying now…' : ' — will send when back online'}
          </div>
        </div>
      )}

      {viewingBn && <BookingDetailDrawer bookingNumber={viewingBn} onClose={() => setViewingBn(null)} />}
    </div>
  );
}

function ResultCard({ children, bg, border, testid }) {
  return (
    <div
      data-testid={testid}
      className="mt-5 rounded-2xl p-5 sm:p-6 border-2"
      style={{ background: bg, borderColor: border }}
    >
      {children}
    </div>
  );
}

function Header({ icon, title, tone }) {
  return (
    <div className="flex items-center gap-3">
      {icon}
      <div className="font-heading text-2xl" style={{ color: tone }}>{title}</div>
    </div>
  );
}

function GuestBlock({ b }) {
  const n = nightsBetween(b.CheckIn, b.CheckOut);
  return (
    <div className="mt-4">
      <div className="font-heading text-3xl sm:text-4xl leading-tight text-[#333]" data-testid="scanner-guest-name">{b.Name}</div>
      <div className="mt-1 text-[#333]/80">
        {b.RoomType} · {b.Guests} guest{b.Guests > 1 ? 's' : ''} · {n} night{n === 1 ? '' : 's'}
      </div>
      <div className="text-xs text-[#333]/60 mt-1 font-mono">{b['Booking#']}</div>
      <div className="text-xs text-[#333]/60 mt-0.5">
        {cwPrettyDate(b.CheckIn)} → {cwPrettyDate(b.CheckOut)}
      </div>
    </div>
  );
}
